// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Music.js
//
// An object representing sheet music.  Contains score-global items
// such as staves, key signature, and time signature.
import { clefNames, isClefName } from 'shared/definitions/musicTheory/clefs';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { TimeSignatureAlias } from 'shared/definitions/musicTheory/rhythms';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import KeySignature from './keySignature';
import Staff from './staff';
import Voice from './voice';
import TimeSignature from './timeSignature';
import utils from '../utils';
import MusicObject from './musicObject';
import type { MidiMessage } from './soundBufferHolder';
import type RhythmCheck from '../uTheory.Vex/rhythmCheck';
import type Interval from './interval';
import type { AriaStringOptions } from './note';
import type CustomKeySignature from './customKeySignature';
import { rhythmSkillStringToRhythmString } from './rhythmSkillStringToRhythmString';

export interface TimeSignatureDefinition {
  timeSignature: TimeSignature;
  measureNumber: number;
  default?: boolean;
}

type MusicalObject = KeySignature | Staff | Voice | TimeSignature;

export default class Music extends MusicObject {
  staves: Staff[] = [];

  voices: Voice[] = [];

  timeSignatureDefinitions: TimeSignatureDefinition[] = [];

  /**
   * Index is meant to represent the measure number, HOWEVER this is
   * not yet implemented, so, for the moment keySignatures is always
   * one item long.
   */
  keySignatures: [KeySignature] = [new KeySignature()];

  rhythmChecks: RhythmCheck[] = [];

  /**
   * For ease of use, staves, timeSignature, and keySignature can
   * be passed to this in any order.  Staves are arranged from
   * top to bottom by the first one passed.
   *
   * If an object is not one of the above, it's tested for containing
   * parameters variables.
   */
  constructor(...musicObjects: (MusicalObject[] | KeySignature | Staff | Voice | TimeSignature)[]) {
    super();
    // Function globals:

    // GCR TO-DO: Change of time signature not yet implemented.
    const defaultTimeSig = new TimeSignature('4/4');
    defaultTimeSig.hidden = true;

    this.timeSignatureDefinitions = [
      {
        timeSignature: defaultTimeSig,
        measureNumber: 0,
        default: true,
      },
    ];

    musicObjects = utils.flatten(musicObjects);
    musicObjects.forEach((musicObject) => {
      if (musicObject instanceof Staff) this.addStaff(musicObject);
      else if (musicObject instanceof Voice)
        this.addVoice(musicObject, musicObject.getStaff() || this.staves[this.staves.length - 1]);
      else if (musicObject instanceof KeySignature) this.setKeySignature(musicObject);
      else if (musicObject instanceof TimeSignature) this.setTimeSignature(musicObject);
    });
  }

  addStaff(staff: Staff): this {
    if (!(staff instanceof Staff)) {
      throw new Error(
        `Not a Staff: music.addStaff can only be called with type staff, not ${
          (staff as any).constructor ? (staff as any).constructor.name : staff
        }`,
      );
    }
    this.staves.push(staff);
    return this;
  }

  removeStaff(staff: Staff): this {
    const index = this.staffToIndex(staff);
    this.staves.splice(index, 1);
    return this;
  }

  staffToIndex(staff: Staff): number {
    if (staff instanceof Staff) {
      return this.staves.indexOf(staff);
    }
    throw new Error('Not a Staff: Staff or index for staves[] must be provided.');
  }

  addVoice(voice: Voice, staff?: Staff): this {
    if (!(voice instanceof Voice)) {
      throw new Error(
        `Not a Voice: addVoice must take a Voice object, not ${
          (voice as any).constructor ? (voice as any).constructor.name : voice
        }`,
      );
    }
    this.voices.push(voice);
    voice.setMusic(this);
    if (staff) this.addVoiceToStaff(voice, staff);
    return this;
  }

  removeVoice(voiceOrIndex: Voice | number): this {
    const index = this.voiceToIndex(voiceOrIndex);
    if (index !== -1) {
      this.voices[index]!.unsetMusic();
      this.voices.splice(index, 1);
    }
    return this;
  }

  voiceToIndex(voiceOrIndex: Voice | number) {
    if (voiceOrIndex instanceof Voice) {
      return this.voices.indexOf(voiceOrIndex);
    }
    if (typeof voiceOrIndex === 'number') return voiceOrIndex;
    throw new Error(
      `Not a Voice: Voice or index for voices[] must be provided, not ${voiceOrIndex}`,
    );
  }

  voiceIndexToStaffIndex(voiceIndex: number): number {
    const voice = this.voices[voiceIndex];
    if (!voice) throw Error(`Invalid voiceIndex: ${voiceIndex}`);
    if (!voice.staff) {
      throw Error(`Voice.staff must be set before calling Music.voiceIndexToStaffIndex`);
    }
    return this.staffToIndex(voice.staff);
  }

  addVoiceToStaff(voiceOrIndex: Voice | number, staff: Staff) {
    const voiceIndex = this.voiceToIndex(voiceOrIndex);
    const voice = this.voices[voiceIndex];
    if (!voice) {
      throw Error('Invalid voiceOrIndex -- could not get this.voices[voiceOrVoiceIndex]');
    }
    const staffIndex = this.staffToIndex(staff);
    const matchedStaff = this.staves[staffIndex];
    if (!matchedStaff) {
      throw Error('Could not find staff in Music.staves for Music.addVoiceToStaff');
    }
    voice.setStaff(matchedStaff);
    return this;
  }

  setKeySignature(keySignature: KeySignature | string, measure = 0): this {
    if (measure !== 0) {
      throw Error('Setting the key signature for any measure other than 0 is not yet allowed.');
    }
    keySignature =
      keySignature instanceof KeySignature ? keySignature : new KeySignature(keySignature);
    this.keySignatures[measure] = keySignature;
    return this;
  }

  setTimeSignature(timeSignature: TimeSignature | TimeSignatureAlias, measureNumber = 0): this {
    timeSignature =
      timeSignature instanceof TimeSignature ? timeSignature : new TimeSignature(timeSignature);

    const oldTimeSignatureDefinition = this.getTimeSignatureDefinition(measureNumber);
    const newTimeSignatureDefinition: TimeSignatureDefinition = {
      timeSignature,
      measureNumber,
      default: false,
    };

    if (oldTimeSignatureDefinition && oldTimeSignatureDefinition.measureNumber === measureNumber) {
      Object.assign(oldTimeSignatureDefinition, newTimeSignatureDefinition);
    } else {
      this.timeSignatureDefinitions = this.timeSignatureDefinitions
        .concat(newTimeSignatureDefinition)
        .sort((a, b) => a.measureNumber - b.measureNumber);
    }
    return this;
  }

  /** Remove the time signature that begins at a measure. If no time signature begins there, returns false */
  removeTimeSignature(measureNumber: number): boolean {
    const index = this.timeSignatureDefinitions.findIndex(
      (tsd) => tsd.measureNumber === measureNumber,
    );
    if (index !== -1) {
      this.timeSignatureDefinitions.splice(index, 1);
      if (this.timeSignatureDefinitions.length === 0) {
        const ts = new TimeSignature('4/4');
        ts.hidden = true;
        this.timeSignatureDefinitions.push({
          timeSignature: ts,
          measureNumber: 0,
          default: true,
        });
      }
      return true;
    }
    return false;
  }

  /** Transposes the notes */
  transpose(interval: Interval | IntervalName) {
    this.keySignatures.forEach((key) => key.transpose(interval));
    this.voices.forEach((voice) => voice.transpose(interval));
  }

  getCustomkeySignature(): CustomKeySignature | false {
    // if any of the staves have a custom key signature, we return it
    const cksStaff = this.staves.find((staff) => staff.customKeySignature);
    return cksStaff ? cksStaff.customKeySignature! : false;
  }

  /** Get the time signature that is active at a certain measure, even if it begins in a previous measure */
  getTimeSignature(measureNumber = 0): TimeSignature | undefined {
    const definition = this.getTimeSignatureDefinition(measureNumber);
    return definition ? definition.timeSignature : undefined;
  }

  /** Return the time signature definition for a sepcified measure. Finds nearest TS definition. */
  getTimeSignatureDefinition(measureNumber = 0) {
    return (
      this.timeSignatureDefinitions
        // Make a copy:
        .slice(0)
        // Arrange the biggest measure numbers first:
        .sort((a, b) => b.measureNumber - a.measureNumber)
        // Then grab the first measure number that's smaller than the requested number:
        .find((ts) => ts.measureNumber <= measureNumber)
    );
  }

  getTotalMeasureNumbers() {
    return Math.max(...this.voices.map((v) => v.measures.length));
  }

  getTotalDurationSeconds() {
    if (!this.tempo) {
      throw Error(`Must set music.tempo to calculate total duration`);
    }
    const ts = this.getTimeSignature() || new TimeSignature();
    return (this.getTotalMeasureNumbers() * ts.beatsPerMeasure * 60) / this.tempo;
  }

  getMidiMessages(): MidiMessage[] {
    return this.voices.map((v) => v.getMidiMessages()).flat();
  }

  toAriaString(ariaOptions: AriaStringOptions = {}) {
    const { keySignature = true, voiceNumber } = ariaOptions;
    let str = '';
    const addSpace = (comma: string = '') => {
      str +=
        str.length && str[str.length - 1] !== ' ' && str[str.length - 1] !== '\n'
          ? `${comma} `
          : '';
    };
    const key = this.getCustomkeySignature() || this.keySignatures[0];
    const staveGroups = utils.groupBy(this.voices, 'staff');
    staveGroups.forEach((staveVoices) => {
      staveVoices.forEach((voice, voiceIndex) => {
        // Start a new line if we have other stuff already:
        str += str.length ? '\n' : '';
        // Name of the clef and voice if needed:
        str += staveVoices[0]!.staff!.toAriaString(ariaOptions);
        if (staveVoices.length > 1 && voiceNumber !== false) {
          str += `, voice ${voiceIndex + 1}`;
        }
        addSpace(';');
        if (keySignature) str += `Key: ${key.toAriaString(ariaOptions)}`;
        addSpace(';');
        const timeSignature = this.getTimeSignature();
        if (timeSignature) {
          str += timeSignature.toAriaString(ariaOptions);
          addSpace(';');
        }
        str += voice.toAriaString(ariaOptions, voice.staff!.clef, key);
      });
    });
    return str;
  }

  /** Music string in musicFile format */
  toString(includeProperties = false) {
    // Basic data, as it exists:
    // Time Signature as time:
    // Key Signature as key:
    // Tempo as tempo:
    let str = '';
    let spacer = '';
    if (this.keySignatures[0]) {
      str += `${spacer}key:${this.keySignatures[0].toMusicFileString()}`;
      spacer = ' ';
    }

    const timeSignature = this.timeSignatureDefinitions[0] && this.getTimeSignature();
    if (timeSignature && !this.timeSignatureDefinitions[0]!.default) {
      str += `${spacer}meter:${timeSignature.toString(true)}`;
      spacer = ' ';
    }

    if (this.tempo) {
      str += `${spacer}tempo:${this.tempo}`;
    }

    if (str !== '') spacer = '\n';

    // For each staff:
    let lastStaff: Staff;
    this.voices.forEach((voice) => {
      // If it's the first voice on a staff, init clef
      if (voice.staff !== lastStaff) {
        str += `${spacer}clef:${voice.staff!.clef} ${voice.toString(includeProperties)}`;
        spacer = '\n';
      } else {
        // It's a 2nd, 3rd, etc... voice, don't need clef:
        str += spacer + voice;
        spacer = '\n';
      }
      lastStaff = voice.staff!;
    }, this);

    return str;
  }

  get isPickup(): boolean {
    return !!this.voices.find((voice) => voice.isPickup);
  }

  /**
   * Takes a rhythm skill string and converts to music. Assumes 12/8 for compound and 4/4 for simple
   * to assure there's always enough room
   * @param skillString in the format C(ompound)/S(imple)/H(cut):e.ser etc...
   */
  static fromSkillString(skillString: string): Music {
    const rhythmString = rhythmSkillStringToRhythmString(skillString);
    return Music.fromRhythmString(rhythmString, { noteName: 'E4' });
  }

  static fromRhythmString(
    rhythmString: string,
    options: { flagPickup?: boolean; stemDirection?: -1 | 0 | 1; noteName?: NoteName } = {},
  ): Music {
    options = {
      flagPickup: false,
      stemDirection: 0,
      noteName: 'B4',
      ...options,
    };
    const rhythmStringParts = rhythmString.split(':');
    const meter = rhythmStringParts[0];
    let musicString = `meter:${meter} key:C/Major`;
    const notesRestsBars = rhythmStringParts.slice(1).join(':').trim().split(' ');
    if (notesRestsBars[0]!.indexOf('tempo') !== -1) {
      const tempo = Number(/tempo=(\d*)/.exec(notesRestsBars[0]!)![1]!);
      musicString += ` tempo:${tempo}`;
      notesRestsBars.splice(0, 1);
    }
    musicString += '\nclef:percussion ';

    musicString += notesRestsBars.reduce((a, b) => {
      const [rhythm, modifierString] = b.split(':');

      if (rhythm!.indexOf('r') !== -1) {
        return `${a} r/${rhythm!.replace(/r/g, '')}${modifierString ? `:${modifierString}` : ''}`;
      }
      if (rhythm!.indexOf('|') !== -1) {
        return `${a} |`;
      }
      if (rhythm!.length > 0) {
        return `${a} ${options.noteName}/${b}`;
      }
      return a;
    }, '');

    const music = Music.fromString(musicString);
    const firstMeasure = music.voices[0]?.measures[0];
    // Check for a pickup measure, and mark it as such:
    if (
      options.flagPickup &&
      firstMeasure &&
      !firstMeasure?.isComplete(music.getTimeSignature()!)
    ) {
      firstMeasure.noteHolders.forEach((nh) => {
        nh.isPickup = !!nh.notes.length;
      });
    }
    if (options.stemDirection) {
      music.voices[0]?.measures.forEach((measure) => {
        measure.noteHolders.forEach((nh) => {
          nh.stemDirection = options.stemDirection!;
        });
      });
    }

    return music;
  }

  static fromString(string: string, music?: Music) {
    // Create or inherit a music object:
    if (!(music instanceof Music)) {
      music = new Music();
    } else {
      // Clear anything that needs clearing...
      music.staves.length = 0;
      music.voices.length = 0;
    }

    // Parse the incoming string into seperate lines:
    const lines = string
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line !== '');

    // Look for, process, and remove global musical parameters
    // including tempo, meter, and key.
    let globalLine = 0;
    const globals = lines[0]!;
    const tempo = /tempo:(\d+)/.exec(globals);
    const meter = /meter:([^\s]+)/.exec(globals);
    const key = /key:([^\s]+)/.exec(globals);

    if (tempo !== null) {
      music.tempo = Number(tempo[1]);
      globalLine = 1;
    }

    if (meter !== null) {
      music.setTimeSignature(new TimeSignature(meter[1] as TimeSignatureAlias));
      globalLine = 1;
    }

    if (key !== null) {
      music.keySignatures[0] = new KeySignature(key[1]!);
      globalLine = 1;
    }

    let lastStaff = null;
    for (let lc = globalLine; lc < lines.length; lc++) {
      // Look for a staff (clef:) definition at start of line,
      // if none is found assume the previously defined staff
      // is the holder staff for this voice.
      let line = lines[lc]!;
      const clefSearch = /clef:([^\s]+)/;
      const clefs = clefSearch.exec(line);
      if (clefs !== null) {
        const clef = clefs[1];
        if (!isClefName(clef)) {
          throw Error(`${clef} is not a valid clefname. Must be one of ${clefNames.join(', ')}`);
        }
        const staff = new Staff(clef);
        music.addStaff(staff);
        lastStaff = staff;
        line = line.replace(clefSearch, '').trim();
      }
      if (lastStaff === null) {
        throw new Error(
          'No Staff: Cannot initialize a music object from string when no staff is provided.',
        );
      }
      // Intialize the voices:
      const voice = Voice.fromString(line);
      voice.setStaff(lastStaff);
      music.addVoice(voice);
    }

    // Return the newly created (or updated) music object:
    return music;
  }
}
